import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { z } from "zod";
import { SiteShell } from "@/components/site-shell";
import { PrintableDocument } from "@/components/printable-document";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UpgradeModal } from "@/components/upgrade-modal";
import { useServerFn } from "@tanstack/react-start";
import { generateDocuments } from "@/lib/documents.functions";
import { PAYMENT_TERMS, PROJECT_TYPES, intakeSchema, type IntakeInput } from "@/lib/intake-schema";
import { DISCIPLINE_BY_SLUG } from "@/lib/disciplines";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, RefreshCw, Sparkles } from "lucide-react";
import { FEATURES, FAQS } from "./index";
import { GoogleAuthButton } from "@/components/google-auth-button";
import { buildNextParam } from "@/lib/auth-redirect";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/generate")({
  head: () => ({
    meta: [
      { title: "Generate a Proposal & Contract — DealDraft" },
      {
        name: "description",
        content:
          "Fill out 10 fields and get a complete, professional freelance proposal and service contract in under 60 seconds. Free.",
      },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      },
    ],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/generate" }],
  }),
  validateSearch: z.object({ type: z.string().optional() }).catch({}),
  component: GeneratePage,
});

const LOADING_MESSAGES = [
  "Analyzing your project...",
  "Writing your proposal...",
  "Drafting your contract...",
  "Finishing up...",
];

const empty: Partial<IntakeInput> = {
  project_type: undefined,
  freelancer_name: "",
  client_name: "",
  client_email: "",
  project_title: "",
  scope: "",
  deliverables: "",
  start_date: "",
  end_date: "",
  total_price: undefined,
  payment_terms: undefined,
};

function GeneratePage() {
  const { user, session, loading: authLoading } = useAuth();
  const generate = useServerFn(generateDocuments);
  const { type } = Route.useSearch();
  const nextParam = buildNextParam("/generate", type ? { type } : undefined);

  const [form, setForm] = useState<Partial<IntakeInput>>(() => {
    const discipline = type ? DISCIPLINE_BY_SLUG.get(type) : undefined;
    return discipline ? { ...empty, project_type: discipline.projectType } : empty;
  });
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const [result, setResult] = useState<{
    proposal: string;
    contract: string;
    documentId: string | null;
  } | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    if (!loading) return;
    let i = 0;
    const t = setInterval(() => {
      i = Math.min(i + 1, LOADING_MESSAGES.length - 1);
      setLoadingMsg(LOADING_MESSAGES[i]);
    }, 3000);
    return () => clearInterval(t);
  }, [loading]);

  function update<K extends keyof IntakeInput>(key: K, val: IntakeInput[K] | undefined) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = intakeSchema.safeParse({
      ...form,
      total_price:
        typeof form.total_price === "string" ? Number(form.total_price) : form.total_price,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please complete all fields.");
      return;
    }
    setLoading(true);
    setLoadingMsg(LOADING_MESSAGES[0]);
    try {
      const headers = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : undefined;
      const res = await generate({ data: parsed.data, headers });
      if (res.error === "limit_reached") {
        setShowUpgrade(true);
        return;
      }
      setResult({
        proposal: res.proposal ?? "",
        contract: res.contract ?? "",
        documentId: res.documentId,
      });
    } catch (err) {
      toast.error((err as Error).message ?? "Generation failed");
    } finally {
      setLoading(false);
    }
  }

  if (!user) {
    return <GenerateLanding nextParam={nextParam} />;
  }

  if (result) {
    return (
      <ResultsView
        result={result}
        meta={form}
        onRegenerate={() => setResult(null)}
        isLoggedIn={!!user}
      />
    );
  }

  return (
    <SiteShell>
      <section className="mx-auto max-w-2xl px-6 py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
          Create Your Proposal & Contract
        </h1>
        <p className="mt-2 text-muted-foreground">
          Fill in the details below. Both documents generate together.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">10 fields · ~2 minutes</p>

        <form
          onSubmit={onSubmit}
          className="mt-8 space-y-5 rounded-xl border border-border bg-card p-6 shadow-sm"
        >
          <Field label="What type of project is this?">
            <Select
              value={form.project_type}
              onValueChange={(v) => update("project_type", v as IntakeInput["project_type"])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a project type" />
              </SelectTrigger>
              <SelectContent>
                {PROJECT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Your name (as it appears on the contract)">
            <Input
              placeholder="Jane Smith"
              value={form.freelancer_name ?? ""}
              onChange={(e) => update("freelancer_name", e.target.value)}
            />
          </Field>

          <Field label="Client name or company">
            <Input
              placeholder="Acme Corp / John Davis"
              value={form.client_name ?? ""}
              onChange={(e) => update("client_name", e.target.value)}
            />
          </Field>

          <Field label="Client email (optional — for your records)">
            <Input
              type="email"
              placeholder="client@example.com"
              value={form.client_email ?? ""}
              onChange={(e) => update("client_email", e.target.value)}
            />
          </Field>

          <Field label="Project title">
            <Input
              placeholder="E.g. Brand Identity Redesign, Company Blog Launch"
              value={form.project_title ?? ""}
              onChange={(e) => update("project_title", e.target.value)}
            />
          </Field>

          <Field
            label="Describe the project scope"
            hint="The more detail you give, the better your documents."
          >
            <Textarea
              rows={4}
              placeholder="What is this project? What problem does it solve? Who is the target audience? Any specific requirements?"
              value={form.scope ?? ""}
              onChange={(e) => update("scope", e.target.value)}
            />
          </Field>

          <Field label="What are you delivering?" hint="List each specific deliverable separately.">
            <Textarea
              rows={3}
              placeholder="E.g. 5 blog posts per month (800 words each), SEO optimized, with royalty-free images sourced and embedded"
              value={form.deliverables ?? ""}
              onChange={(e) => update("deliverables", e.target.value)}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Project start date">
              <Input
                type="date"
                value={form.start_date ?? ""}
                onChange={(e) => update("start_date", e.target.value)}
              />
            </Field>
            <Field label="Project completion / deadline">
              <Input
                type="date"
                value={form.end_date ?? ""}
                onChange={(e) => update("end_date", e.target.value)}
              />
            </Field>
          </div>

          <Field label="Total project fee (USD)">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                type="number"
                min={0}
                step="1"
                placeholder="2500"
                className="pl-7"
                value={form.total_price ?? ""}
                onChange={(e) =>
                  update("total_price", e.target.value === "" ? undefined : Number(e.target.value))
                }
              />
            </div>
          </Field>

          <Field label="Payment terms">
            <Select
              value={form.payment_terms}
              onValueChange={(v) => update("payment_terms", v as IntakeInput["payment_terms"])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select payment terms" />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_TERMS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Button type="submit" size="lg" className="h-12 w-full text-base" disabled={loading || authLoading} data-testid="generate-submit">
            {loading ? (
              <span className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 animate-pulse" /> {loadingMsg}
              </span>
            ) : (
              <>✨ Generate My Proposal & Contract</>
            )}
          </Button>
          {loading && (
            <p className="text-center text-xs text-muted-foreground">
              This usually takes 8–15 seconds.
            </p>
          )}
        </form>
      </section>

      <UpgradeModal open={showUpgrade} onOpenChange={setShowUpgrade} />
    </SiteShell>
  );
}

function GenerateLanding({ nextParam }: { nextParam: string }) {
  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 pt-16 pb-12 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          Create Your Proposal & Contract
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground sm:text-lg">
          Sign in free to generate a professional proposal and contract in 60 seconds. No credit
          card required.
        </p>
        <div className="mt-8 flex flex-col items-center gap-3">
          <div className="w-full max-w-xs">
            <GoogleAuthButton label="Create a Proposal" next={nextParam} />
          </div>
          <p className="text-sm text-muted-foreground">
            or{" "}
            <Link
              to="/signup"
              search={{ next: nextParam }}
              className="font-medium text-primary hover:underline"
            >
              sign up with email
            </Link>
          </p>
        </div>
      </section>

      <section className="bg-secondary/40 py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
            Everything You Need to Send a Proposal
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="text-2xl">{f.emoji}</div>
                <h3 className="mt-3 text-base font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-16">
        <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
          Common Questions
        </h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f) => (
            <AccordionItem key={f.q} value={f.q}>
              <AccordionTrigger className="text-left text-base font-medium">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </SiteShell>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function ResultsView({
  result,
  meta,
  onRegenerate,
  isLoggedIn,
}: {
  result: { proposal: string; contract: string; documentId: string | null };
  meta: Partial<IntakeInput>;
  onRegenerate: () => void;
  isLoggedIn: boolean;
}) {
  const [proposal, setProposal] = useState(result.proposal);
  const [contract, setContract] = useState(result.contract);
  const [tab, setTab] = useState("proposal");

  function copyActive() {
    const text = tab === "proposal" ? proposal : contract;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  function downloadPdf() {
    window.print();
  }

  return (
    <SiteShell>
      <section className="mx-auto max-w-3xl px-6 py-10 print:hidden">
        <h1 className="text-3xl font-semibold tracking-tight">✅ Your Documents Are Ready</h1>
        <p className="mt-2 text-muted-foreground">
          Review, edit, and download your proposal and contract below.
        </p>

        {isLoggedIn && result.documentId && (
          <div className="mt-6 rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-sm">
            ✅ <strong>Document saved.</strong>{" "}
            <Link
              to="/documents/$id"
              params={{ id: result.documentId }}
              className="underline hover:no-underline"
            >
              View it on your dashboard →
            </Link>
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList>
            <TabsTrigger value="proposal">📄 Client Proposal</TabsTrigger>
            <TabsTrigger value="contract">📋 Service Contract</TabsTrigger>
          </TabsList>
          <TabsContent value="proposal">
            <DocEditor value={proposal} onChange={setProposal} />
          </TabsContent>
          <TabsContent value="contract">
            <DocEditor value={contract} onChange={setContract} />
          </TabsContent>
        </Tabs>

        <div className="no-print sticky bottom-4 mt-6 flex flex-wrap gap-2 rounded-xl border border-border bg-card/95 p-3 shadow-md backdrop-blur">
          <Button onClick={downloadPdf}>
            <Download className="h-4 w-4" /> Download as PDF
          </Button>
          <Button variant="outline" onClick={copyActive}>
            <Copy className="h-4 w-4" /> Copy
          </Button>
          <Button variant="outline" onClick={onRegenerate}>
            <RefreshCw className="h-4 w-4" /> Re-generate
          </Button>
        </div>
      </section>

      <PrintableDocument
        proposal={proposal}
        contract={contract}
        meta={{
          projectTitle: meta.project_title,
          clientName: meta.client_name,
          freelancerName: meta.freelancer_name,
          projectType: meta.project_type,
          totalPrice: meta.total_price,
          startDate: meta.start_date,
          endDate: meta.end_date,
        }}
      />
    </SiteShell>
  );
}

function DocEditor({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-5 shadow-sm">
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[480px] resize-y border-0 p-0 text-base leading-relaxed focus-visible:ring-0"
      />
      <p className="mt-3 text-xs text-muted-foreground">
        {value.length.toLocaleString()} characters ·{" "}
        {value.trim().split(/\s+/).filter(Boolean).length.toLocaleString()} words
      </p>
    </div>
  );
}
